/**
 * Accettazione T&C — shell Apple, navigazione libera.
 * Scroll obbligatorio + due flag + conferma. Toggle ENG (vincolante, predefinito) / ITA.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, FileDown } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { OnboardingShell } from "@/components/onboarding/OnboardingShell";
import {
  acceptOnboardingTerms,
  downloadOnboardingContractPdf,
  fetchOnboardingByToken,
  type OnboardingPublicPayload,
} from "@/lib/onboarding-public";
import {
  formatOnboardingEconomicSummary,
  formatOnboardingMoney,
  ONBOARDING_PAYMENT_METHOD_LABELS,
  ONBOARDING_SETUP_FEE_LABEL,
  onboardingBillingPeriodLabel,
  resolveOnboardingPaymentMethod,
} from "@/types/onboarding";
import { cn } from "@/lib/utils";

type CgsLang = "it" | "en";

export default function OnboardingTermsPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrolledToEnd, setScrolledToEnd] = useState(false);
  const [flagCgs, setFlagCgs] = useState(false);
  const [flagArt50, setFlagArt50] = useState(false);
  const [lang, setLang] = useState<CgsLang>("en");

  const { data, isLoading, error } = useQuery({
    queryKey: ["onboarding-public", token],
    enabled: !!token,
    queryFn: () => fetchOnboardingByToken(token!),
    retry: false,
  });

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const atEnd = el.scrollTop + el.clientHeight >= el.scrollHeight - 24;
    if (atEnd) setScrolledToEnd(true);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !data?.cgs) return;
    el.scrollTop = 0;
    requestAnimationFrame(() => {
      if (el.scrollHeight <= el.clientHeight + 8) setScrolledToEnd(true);
    });
  }, [lang, data?.cgs]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !data?.cgs) return;
    if (el.scrollHeight <= el.clientHeight + 8) setScrolledToEnd(true);
  }, [data?.cgs]);

  const acceptMutation = useMutation({
    mutationFn: () => acceptOnboardingTerms(token!),
    onSuccess: async () => {
      queryClient.setQueryData(
        ["onboarding-public", token],
        (prev: OnboardingPublicPayload | undefined) => {
          if (!prev) return prev;
          return {
            ...prev,
            onboarding: {
              ...prev.onboarding,
              terms_accepted_at: new Date().toISOString(),
              current_step: "payment",
              has_contract_pdf: true,
            },
          };
        },
      );
      await queryClient.invalidateQueries({ queryKey: ["onboarding-public", token] });
      toast.success("Contratto accettato");
      navigate(`/onboarding/${token}/pagamento`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const downloadPdfMutation = useMutation({
    mutationFn: (pdfLang: CgsLang) => downloadOnboardingContractPdf(token!, pdfLang),
    onSuccess: ({ url }) => {
      window.open(url, "_blank", "noopener,noreferrer");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const alreadyAccepted = !!data?.onboarding.terms_accepted_at;
  const canConfirm =
    !alreadyAccepted && scrolledToEnd && flagCgs && flagArt50 && !acceptMutation.isPending;

  if (!token) return null;

  if (isLoading || error || !data) {
    return (
      <OnboardingShell
        token={token}
        step="terms"
        loading={isLoading}
        error={error instanceof Error ? error.message : error ? "Errore" : null}
        hideBack
        hideContinue
        wide
      >
        {null}
      </OnboardingShell>
    );
  }

  const { onboarding, cgs } = data;
  const termsAccepted = !!onboarding.terms_accepted_at;
  const clausesIt = Array.isArray(cgs?.art50_clauses) ? (cgs!.art50_clauses as string[]) : [];
  const clausesEn = Array.isArray(cgs?.art50_clauses_en) ? cgs!.art50_clauses_en : [];
  const clauses = lang === "en" && clausesEn.length > 0 ? clausesEn : clausesIt;
  const bodyHtml = lang === "en" ? cgs?.body_html_en || "" : cgs?.body_html || "";
  const allegatiHtml =
    lang === "en" ? cgs?.allegati_html_en || "" : cgs?.allegati_html || "";
  const hasEn = !!(cgs?.body_html_en && cgs.body_html_en.trim().length > 40);
  const langLabel = lang === "en" ? "ENG" : "ITA";

  return (
    <OnboardingShell
      token={token}
      step="terms"
      tenantName={onboarding.tenant_name}
      payload={data}
      wide
      hideBack
      continueLabel={termsAccepted ? "Continua" : "Conferma e continua"}
      continueDisabled={termsAccepted ? false : !canConfirm}
      onContinue={() => {
        if (termsAccepted) {
          navigate(`/onboarding/${token}/pagamento`);
          return;
        }
        acceptMutation.mutate();
      }}
    >
      <p className="text-sm text-muted-foreground leading-relaxed">
        {termsAccepted
          ? "Contratto già accettato. Puoi rileggerlo in qualsiasi momento."
          : "Leggi le condizioni, conferma i flag e prosegui al pagamento."}
      </p>

      <section className="rounded-2xl border border-border/70 bg-card p-5 space-y-3">
        <h2 className="text-sm font-semibold">Condizioni economiche</h2>
        <p className="text-2xl font-semibold tabular-nums">
          {
            formatOnboardingEconomicSummary(
              onboarding.price_amount,
              onboarding.billing_period,
              onboarding.setup_fee_amount ?? 0,
              onboarding.currency,
              onboarding.billing_years,
            ).split(" + ")[0]
          }
        </p>
        {(onboarding.setup_fee_amount ?? 0) > 0 && (
          <p className="text-lg font-semibold tabular-nums text-muted-foreground">
            + {formatOnboardingMoney(onboarding.setup_fee_amount, onboarding.currency)}{" "}
            <span className="text-sm font-medium">{ONBOARDING_SETUP_FEE_LABEL}</span>
          </p>
        )}
        <p className="text-xs text-muted-foreground">
          Periodicità:{" "}
          {onboardingBillingPeriodLabel(
            onboarding.billing_period,
            onboarding.billing_years,
          )}{" "}
          · Pagamento:{" "}
          {
            ONBOARDING_PAYMENT_METHOD_LABELS[
              resolveOnboardingPaymentMethod(onboarding.payment_method)
            ]
          }{" "}
          · Valuta {onboarding.currency.toUpperCase()}
          {onboarding.signer_email
            ? ` · Firmatario: ${onboarding.signer_name || onboarding.signer_email}`
            : ""}
        </p>
      </section>

      <div className="rounded-2xl border border-amber-200 bg-amber-50/80 px-4 py-3 text-sm text-amber-950 leading-relaxed">
        <p>
          <strong>Lingua del Contratto (art. 49):</strong> l&apos;unico testo vincolante è la
          versione in <strong>inglese</strong>. La versione italiana è una traduzione di cortesia.
        </p>
      </div>

      <section className="overflow-hidden rounded-2xl border border-border/70 bg-card shadow-sm">
        <div className="flex flex-col gap-3 border-b border-border/60 bg-stone-50/80 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-semibold">CGS e Allegati</p>
            <p className="text-xs text-muted-foreground">
              {cgs ? `Versione ${cgs.version}` : "CGS"}
              {lang === "en" ? " · English (binding)" : " · Italiano (cortesia)"}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="rounded-xl h-9"
              disabled={!termsAccepted || downloadPdfMutation.isPending}
              onClick={() => downloadPdfMutation.mutate(lang)}
            >
              {downloadPdfMutation.isPending ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <FileDown className="mr-1.5 h-3.5 w-3.5" />
              )}
              Scarica contratto ({langLabel})
            </Button>
            <div
              className="inline-flex rounded-full border border-border/80 bg-white p-0.5 shadow-sm"
              role="group"
              aria-label="Lingua"
            >
              {(["en", "it"] as const).map((l) => (
                <button
                  key={l}
                  type="button"
                  onClick={() => setLang(l)}
                  className={cn(
                    "rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors",
                    lang === l
                      ? "bg-stone-900 text-white"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {l === "it" ? "ITA" : "ENG"}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div
          ref={scrollRef}
          onScroll={checkScroll}
          className={cn(
            "max-h-[min(58vh,520px)] overflow-y-auto overflow-x-auto bg-white px-4 py-5 sm:px-6",
            "[&_h1]:mb-2 [&_h1]:mt-0 [&_h1]:text-[1.2rem] [&_h1]:font-semibold [&_h1]:tracking-tight",
            "[&_h2]:mb-3 [&_h2]:mt-8 [&_h2]:border-b [&_h2]:border-slate-100 [&_h2]:pb-2 [&_h2]:text-[0.85rem] [&_h2]:font-semibold [&_h2]:uppercase [&_h2]:tracking-wide [&_h2]:text-slate-700",
            "[&_p]:mb-3 [&_p]:text-[13px] [&_p]:leading-[1.65] [&_p]:text-slate-600",
            "[&_ol]:my-3 [&_ol]:space-y-1.5 [&_ol]:pl-5 [&_ol]:text-[13px] [&_ol]:text-slate-600",
            "[&_ul]:my-3 [&_ul]:space-y-1.5 [&_ul]:pl-5 [&_ul]:text-[13px] [&_ul]:text-slate-600",
            "[&_table]:my-4 [&_table]:w-full [&_table]:min-w-[32rem] [&_table]:border-collapse [&_table]:text-[12px]",
            "[&_th]:border [&_th]:border-slate-200 [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:font-semibold",
            "[&_td]:border [&_td]:border-slate-200 [&_td]:px-3 [&_td]:py-2",
          )}
        >
          {cgs && bodyHtml ? (
            <article>
              <div dangerouslySetInnerHTML={{ __html: bodyHtml }} />
              {allegatiHtml ? (
                <>
                  <hr className="my-8 border-slate-100" />
                  <div dangerouslySetInnerHTML={{ __html: allegatiHtml }} />
                </>
              ) : null}
            </article>
          ) : (
            <p className="text-sm text-slate-400">
              {lang === "en" && !hasEn
                ? "English version not available yet."
                : "Testo CGS non disponibile."}
            </p>
          )}
        </div>
        {!scrolledToEnd && !termsAccepted ? (
          <p className="border-t border-border/60 px-4 py-2 text-center text-[11px] text-amber-700 bg-amber-50/50">
            Scorri il documento fino in fondo per abilitare i flag
          </p>
        ) : null}
      </section>

      {!termsAccepted ? (
        <section
          className={cn(
            "rounded-2xl border border-border/70 bg-card p-5 space-y-4",
            !scrolledToEnd && "opacity-60",
          )}
        >
          <div className="flex items-start gap-3">
            <Checkbox
              id="flag-cgs"
              checked={flagCgs}
              disabled={!scrolledToEnd || acceptMutation.isPending}
              onCheckedChange={(v) => setFlagCgs(v === true)}
              className="mt-0.5"
            />
            <Label htmlFor="flag-cgs" className="text-sm font-normal leading-snug cursor-pointer">
              Accetto le <strong>Condizioni Generali</strong> e gli <strong>Allegati</strong>,
              incluso il testo vincolante in inglese.
            </Label>
          </div>
          <div className="flex items-start gap-3">
            <Checkbox
              id="flag-art50"
              checked={flagArt50}
              disabled={!scrolledToEnd || acceptMutation.isPending}
              onCheckedChange={(v) => setFlagArt50(v === true)}
              className="mt-0.5"
            />
            <div className="space-y-2">
              <Label
                htmlFor="flag-art50"
                className="text-sm font-normal leading-snug cursor-pointer block"
              >
                Approvo specificamente le clausole art. 50 (artt. 1341–1342 c.c.):
              </Label>
              <ul className="list-disc pl-4 text-xs text-muted-foreground space-y-1">
                {clauses.map((c) => (
                  <li key={c}>{c}</li>
                ))}
              </ul>
            </div>
          </div>
          {acceptMutation.isPending ? (
            <p className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Registrazione in corso…
            </p>
          ) : (
            <p className="text-[11px] text-muted-foreground">
              Verranno registrati data, ora, IP, utente e versione CGS.
            </p>
          )}
        </section>
      ) : (
        <p className="text-sm text-emerald-700 font-medium">Contratto accettato.</p>
      )}
    </OnboardingShell>
  );
}
